/**
 * `useSourceAuth` — React hook that loads the decrypted `SourceAuth`
 * for a registered source on mount, returning a `{ auth, ready }`
 * pair. Consumers wait on `ready` before kicking off source-module
 * calls so the cookies are present in the very first request.
 *
 * Runtime wiring (`PassphraseSession.isUnlocked` + `getAuth`) lives
 * here so the underlying `loadSourceAuth` module stays free of any
 * `@/`-aliased value imports and remains directly runnable under
 * `node --test`. See `loadSourceAuth.ts` for the failure-mode policy.
 */

import { useEffect, useRef, useState } from 'react';

import { useAccountAuthReady } from '@/features/account';
import { subscribeAccountDataRestored } from '@/features/account/accountDataEvents';
import type { ComicSource, SourceAuth } from '@/sources/types';

import { useSourceAuthLoader } from './useSourceAuthLoader';

export interface UseSourceAuthState {
  /** The decrypted `SourceAuth` blob, or undefined if unavailable. */
  auth: SourceAuth | undefined;
  /**
   * `false` until the initial load completes (success OR failure).
   * Effects that fetch on mount should gate on this — otherwise the
   * first request fires without cookies and the SPA's anonymous
   * fallback path runs even though the user is logged in.
   */
  ready: boolean;
}

export function useSourceAuth(source: ComicSource | undefined): UseSourceAuthState {
  const loadAuth = useSourceAuthLoader();
  const accountAuthReady = useAccountAuthReady();
  const [state, setState] = useState<UseSourceAuthState>({ auth: undefined, ready: false });
  const lastSourceId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!source) {
      lastSourceId.current = undefined;
      setState({ auth: undefined, ready: true });
      return;
    }
    if (!accountAuthReady) {
      setState({ auth: undefined, ready: false });
      return;
    }
    let cancelled = false;
    let loadVersion = 0;
    const sourceChanged = lastSourceId.current !== source.id;
    lastSourceId.current = source.id;
    if (sourceChanged) {
      setState({ auth: undefined, ready: false });
    }
    const reload = () => {
      const version = ++loadVersion;
      loadAuth(source).then((auth) => {
        if (cancelled || version !== loadVersion) return;
        setState({ auth, ready: true });
      });
    };
    reload();
    const unsubscribe = subscribeAccountDataRestored(reload);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [source, loadAuth, accountAuthReady]);

  return state;
}
